<template>
  <div class="ja-content-image-resize">
    <common-header>
      <template #header>
        <span>コンテンツ</span>
      </template>
    </common-header>
    <div class="content-image-resize-area">
      <panZoom @init="onInit" :options="panZoomOptions" selector=".material-image">
        <div class="content-recording-area" :style="thisAspectRatio" ref="contentRecordingArea">
          <img
            class="material-image"
            ref="materialImage"
            :src="materialImageSrc"
            alt="material image"
            @load="getOriginalImageSize"
          />
        </div>
      </panZoom>
      <div class="content-recording-grid-area" :style="thisAspectRatio">
        <div class="content-recording-grid content-recording-grid-top-left"></div>
        <div class="content-recording-grid"></div>
        <div class="content-recording-grid content-recording-grid-top-right"></div>
        <div class="content-recording-grid"></div>
        <div class="content-recording-grid"></div>
        <div class="content-recording-grid"></div>
        <div class="content-recording-grid content-recording-grid-bottom-left"></div>
        <div class="content-recording-grid"></div>
        <div class="content-recording-grid content-recording-grid-bottom-right"></div>
      </div>
    </div>
    <common-footer class="common-footer d-flex">
      <template #footer>
        <v-ons-row class="footer-button-area">
          <small-button
            class="footer-back-button"
            title="戻る"
            :disabled="false"
            @click="() => moveBackButton(router)"
          />
          <small-button
            class="footer-submit-button"
            title="このサイズにする"
            @click="submitContentSize"
          />
        </v-ons-row>
      </template>
    </common-footer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import SmallButton from '@/components/common/SmallButton.vue';
import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import { moveBackButton } from '@/composable/common';
import { TEMPLATES } from '@/composable/constant_common';
import { AZURE_CDN } from '@/composable/constant';
import { getThumbnailUrl } from '@/composable/material_common';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contentRecordingArea = ref(null);
const materialImage = ref(null);

// 素材番号
const materialNum = ref(route.params.materialNum);

// panZoomのインスタンス
const panZoomInstance = ref(null);

// panZoomの初期化オプション
const panZoomOptions = reactive({
  maxZoom: 10,
  minZoom: 0.1,
  initialX: 0,
  initialY: 0,
  initialZoom: 1,
  bounds: true,
  boundsPadding: 0.1,
});

// オフセットX
const offsetX = ref(0);

// オフセットY
const offsetY = ref(0);

// スケール
const scale = ref(1);

// content-recording-areaの幅
const contentRecordingAreaWidth = ref(0);

// content-recording-areaの高さ
const contentRecordingAreaHeight = ref(0);

// オリジナル画像の幅
const originalImageWidth = ref(0);

// オリジナル画像の高さ
const originalImageHeight = ref(0);

// panZoom上のスケール(オリジナル画像と同じサイズの表示にするための倍率)
const panZoomScale = ref(1);

/**
 * テンプレートID
 */
const templateId = computed(() => store.getters['content/getTemplateId']);
/**
 * 素材URL
 */
const materialUrl = computed(() => {
  return store.getters['content/getContentMaterialList'][materialNum.value].materialUrl;
});

/**
 * アスペクト比
 */
const aspectRatio = computed(
  () => TEMPLATES[templateId.value].ASPECT_RATIO_LIST[materialNum.value]
);
const thisAspectRatio = computed(() => ({
  '--aspect-ratio': aspectRatio.value,
}));

/**
 * panZoom記録エリアのサイズ取得処理
 */
const getContentRecordingAreaSize = () => {
  const element = contentRecordingArea.value;
  if (!element) return;
  contentRecordingAreaWidth.value = element.getBoundingClientRect().width;
  contentRecordingAreaHeight.value = element.getBoundingClientRect().height;
};

/**
 * オリジナル画像サイズ取得処理
 */
const getOriginalImageSize = (event) => {
  originalImageWidth.value = event.target.naturalWidth;
  originalImageHeight.value = event.target.naturalHeight;
};

/**
 * panZoom初期化時処理
 */
const onInit = (instance) => {
  panZoomInstance.value = instance;
};

/**
 * panZoom設定処理
 */
const setupPanZoom = () => {
  if (originalImageWidth.value > 0) {
    // panZoomで基点(0, 0)、スケール1で表示した際の画像のサイズからpanZoom上のスケールを算出
    panZoomInstance.value.moveTo(0, 0);
    panZoomInstance.value.zoomTo(0, 0, 1);
    setTimeout(() => {
      const element = materialImage.value;
      if (!element) return;
      const materialImageWidth = element.getBoundingClientRect().width;
      panZoomScale.value = originalImageWidth.value / materialImageWidth;

      panZoomInstance.value.zoomTo(
        0,
        0,
        ((scale.value * contentRecordingAreaWidth.value) / originalImageWidth.value) *
          panZoomScale.value
      );
      panZoomInstance.value.moveTo(
        offsetX.value * contentRecordingAreaWidth.value,
        offsetY.value * contentRecordingAreaHeight.value
      );
    }, 1);
  }
};

/**
 * このサイズにするボタン押下時処理
 */
const submitContentSize = () => {
  const transForm = panZoomInstance.value.getTransform();
  offsetX.value = transForm.x / contentRecordingAreaWidth.value;
  offsetY.value = transForm.y / contentRecordingAreaHeight.value;
  scale.value = (transForm.scale * originalImageWidth.value) / contentRecordingAreaWidth.value;

  const contentMaterialList = store.getters['content/getContentMaterialList'];
  contentMaterialList[materialNum.value].offsetX = offsetX.value;
  contentMaterialList[materialNum.value].offsetY = offsetY.value;
  contentMaterialList[materialNum.value].scale = scale.value;
  store.commit('content/setContentMaterialList', contentMaterialList);
  moveBackButton(router);
};

/**
 * 画像サムネイルURLの算出（外部CDNパス等を合成）
 */
const materialImageSrc = computed(() => {
  const url = materialUrl.value;
  if (!url) return '';
  return AZURE_CDN.ENDPOINT + getThumbnailUrl(url) + AZURE_CDN.SAS;
});

onMounted(() => {
  const contentMaterial = store.getters['content/getContentMaterialList'][materialNum.value];
  offsetX.value = contentMaterial.offsetX;
  offsetY.value = contentMaterial.offsetY;
  scale.value = contentMaterial.scale;
  getContentRecordingAreaSize();
  window.addEventListener('resize', getContentRecordingAreaSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', getContentRecordingAreaSize);
});

watch(
  [contentRecordingAreaWidth, originalImageWidth],
  () => {
    setupPanZoom();
  },
  { deep: true }
);
</script>

<style>
html:has(.ja-content-image-resize) {
  overflow: hidden !important;
}
</style>

<style scoped>
.ja-content-image-resize {
  margin: auto;
}
.content-image-resize-area {
  box-sizing: border-box;
  background-color: #000;
  padding-top: 10vh;
  padding-top: 10dvh;
  height: 100vh;
  height: 100dvh;
  z-index: 1;
  position: relative;
  overflow: hidden;
}
.content-recording-area,
.content-recording-grid-area {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  width: 90vw;
  width: min(60vh * var(--aspect-ratio), 90vw);
  width: min(60dvh * var(--aspect-ratio), 90vw);
  max-width: 840px;
  height: min(60vh, 90vw / var(--aspect-ratio));
  height: min(60dvh, 90vw / var(--aspect-ratio));
  max-height: calc(840px / var(--aspect-ratio));
}
.content-recording-grid-area {
  z-index: 2;
  display: grid;
  grid-template-rows: repeat(3, calc(100% / 3));
  grid-template-columns: repeat(3, calc(100% / 3));
  pointer-events: none;
}
.content-recording-grid {
  border: solid 1px #fff;
  position: relative;
  pointer-events: none;
}
.content-recording-grid-top-left::after,
.content-recording-grid-top-right::after,
.content-recording-grid-bottom-left::after,
.content-recording-grid-bottom-right::after {
  content: '';
  width: 30px;
  height: 30px;
  position: absolute;
}
.content-recording-grid-top-left::after {
  border-left: 2px solid #fff;
  border-top: 2px solid #fff;
  top: 0;
  left: 0;
}
.content-recording-grid-top-right::after {
  border-right: 2px solid #fff;
  border-top: 2px solid #fff;
  top: 0;
  right: 0;
}
.content-recording-grid-bottom-left::after {
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  left: 0;
  bottom: 0;
}
.content-recording-grid-bottom-right::after {
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  right: 0;
  bottom: 0;
}
.material-image {
  z-index: 1;
}
label {
  display: block;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
}
p {
  font-size: 1.5rem;
  margin: 0;
}
.common-footer {
  height: 10rem;
  flex-direction: column;
}
.footer-button-area {
  height: 100%;
  width: 90%;
  padding-bottom: 1rem;
}
.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 25%;
}
.footer-submit-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 65%;
}
</style>
